/**
 * Angebot oder Rechnung am Telefon — nur ansehen (20.10.2026).
 *
 * Benjamins Vorgabe: am Telefon Belege öffnen und lesen, bearbeitet wird am
 * Computer. Die drei Editor-Adressen landen am Telefon hier:
 *  - Entwurf      → Kopf, Positionen, Summen und „Vorschau ansehen" (PDF als Seitenbilder),
 *  - festgeschrieben/bearbeiten → das fertige Dokument,
 *  - neu          → Hinweis, dass neue Belege am Computer entstehen.
 */
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DokumentLightbox, { type Dokument } from "../components/DokumentLightbox";
import { previewInvoicePdf, useInvoiceDocument, useWebInvoiceDraft } from "../hooks/useInvoices";
import { normalisiereDraft } from "../lib/invoiceEditor";
import { belegArtLabel, errorText, euro, invoiceDokument, type WebInvoiceLine } from "../lib/invoices";
import { seitenbilderAus } from "../lib/seitenbilder";
import "../styles/beleg-ansicht.css";

export default function BelegAnsichtTelefon() {
  const { draftId, invoiceId } = useParams();
  if (draftId) return <EntwurfAnsicht id={draftId} />;
  if (invoiceId) return <BelegAnsicht id={invoiceId} />;
  return (
    <Rahmen titel="Neuer Beleg">
      <p className="ba-hinweis">
        Neue Angebote und Rechnungen legst du am Computer an. Am Telefon kannst du Belege
        ansehen, versenden, ihren Status setzen und sie einem Projekt zuordnen.
      </p>
    </Rahmen>
  );
}

function Rahmen({ titel, kicker, unterzeile, children }: {
  titel: string; kicker?: string; unterzeile?: string; children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <div className="ba-seite">
      <button type="button" className="ba-zurueck" onClick={() => navigate(-1)}>‹ Zurück</button>
      <header className="ba-kopf">
        {kicker && <p className="ba-kicker">{kicker}</p>}
        <h1>{titel}</h1>
        {unterzeile && <p className="ba-unter">{unterzeile}</p>}
      </header>
      {children}
      <Link className="ba-link" to="/rechnungen">Alle Angebote &amp; Rechnungen</Link>
    </div>
  );
}

const AM_COMPUTER = "Bearbeiten geht am Computer — am Telefon nur ansehen.";

function EntwurfAnsicht({ id }: { id: string }) {
  const q = useWebInvoiceDraft(id);
  const [dok, setDok] = useState<Dokument>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState("");

  if (q.isLoading) return <Rahmen titel="Entwurf"><p className="ba-hinweis">Lade Entwurf …</p></Rahmen>;
  if (q.isError || !q.data) {
    return <Rahmen titel="Entwurf"><p className="ba-fehler">{q.error ? errorText(q.error) : "Entwurf nicht gefunden."}</p></Rahmen>;
  }
  const r = q.data;
  const doc = normalisiereDraft(r.document);
  const art = belegArtLabel(r.kind, doc.source_quote_number);
  const titel = doc.doc_title || doc.subject || art;

  const vorschau = async () => {
    setLaedt(true);
    setFehler("");
    try {
      const ergebnis = await previewInvoicePdf(doc);
      const url = URL.createObjectURL(ergebnis.blob);
      const seiten = await seitenbilderAus(ergebnis.blob);
      setDok({ titel: `${art} (Entwurf)`, unterzeile: r.kunde_name || undefined, url, seitenBilder: seiten });
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setLaedt(false);
    }
  };

  return (
    <Rahmen titel={titel} kicker={`${art} · Entwurf`} unterzeile={[r.kunde_name, r.projekt_title].filter(Boolean).join(" · ")}>
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
      <button type="button" className="ba-knopf" onClick={vorschau} disabled={laedt}>
        {laedt ? "Vorschau wird erzeugt …" : "Vorschau ansehen"}
      </button>
      {fehler && <p className="ba-fehler">{fehler}</p>}
      <p className="ba-hinweis">{AM_COMPUTER}</p>
      <Positionen zeilen={doc.line_rows || []} />
      <Summen netto={r.netto} mwst={r.mwst} brutto={r.brutto} />
    </Rahmen>
  );
}

function BelegAnsicht({ id }: { id: string }) {
  const q = useInvoiceDocument(id);
  const [dok, setDok] = useState<Dokument>(null);
  if (q.isLoading) return <Rahmen titel="Beleg"><p className="ba-hinweis">Lade Beleg …</p></Rahmen>;
  if (q.isError || !q.data) {
    return <Rahmen titel="Beleg"><p className="ba-fehler">{q.error ? errorText(q.error) : "Beleg nicht gefunden."}</p></Rahmen>;
  }
  const b = q.data;
  const art = belegArtLabel(b.kind, b.web_document_json?.source_quote_number);
  return (
    <Rahmen titel={b.nummer || art} kicker={art} unterzeile={[b.kunde_name, b.projekt_title].filter(Boolean).join(" · ")}>
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
      <button type="button" className="ba-knopf"
        onClick={() => setDok(invoiceDokument({ id: b.id, nummer: b.nummer, kind: b.kind, web_pdf_sha256: "", updated_at: "" }, b.kunde_name))}>
        Dokument ansehen
      </button>
      <p className="ba-hinweis">{AM_COMPUTER}</p>
      {b.web_document_json && <Positionen zeilen={b.web_document_json.line_rows || []} />}
    </Rahmen>
  );
}

function Positionen({ zeilen }: { zeilen: WebInvoiceLine[] }) {
  if (!zeilen.length) return null;
  return (
    <ul className="ba-positionen">
      {zeilen.map((z, i) => z.line_kind === "section" ? (
        <li key={z.id || i} className="ba-abschnitt">{z.title}</li>
      ) : (
        <li key={z.id || i} className={z.line_kind === "optional" ? "is-optional" : undefined}>
          <span className="ba-pos-titel">{z.title}{z.line_kind === "optional" && <em> · optional</em>}</span>
          <span className="ba-pos-menge">
            {z.quantity.toLocaleString("de-DE")} {z.unit_label} × {euro(z.unit_price_net)}
          </span>
          <strong className="ba-pos-summe">{euro(z.quantity * z.unit_price_net)}</strong>
        </li>
      ))}
    </ul>
  );
}

function Summen({ netto, mwst, brutto }: { netto: number; mwst: number; brutto: number }) {
  return (
    <dl className="ba-summen">
      <div><dt>Netto</dt><dd>{euro(netto)}</dd></div>
      <div><dt>USt.</dt><dd>{euro(mwst)}</dd></div>
      <div className="is-gesamt"><dt>Gesamt</dt><dd>{euro(brutto)}</dd></div>
    </dl>
  );
}
